// src/pages/Login.tsx
import React, { useState } from 'react';
import { Card, Form, Button, Alert, Container, Row, Col } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaSignInAlt, FaLock } from 'react-icons/fa';

interface LoginValues {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  // Obter o caminho de redirecionamento após o login
  const from = location.state?.from?.pathname || '/dashboard';

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Email inválido')
      .required('Email é obrigatório'),
    password: Yup.string()
      .required('Senha é obrigatória')
  });

  const handleSubmit = async (
    values: LoginValues, 
    { setSubmitting }: FormikHelpers<LoginValues>
  ) => {
    try {
      setError(null);
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError('Credenciais inválidas. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Row className="justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Col md={6} lg={5} xl={4}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="SAC Logo" 
                  width="80" 
                  className="mb-3" 
                />
                <h2 className="mb-0">Bem-vindo</h2>
                <p className="text-muted">Sistema de Tarifas de Água</p>
              </div>

              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Seu email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Senha</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="Sua senha"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && !!errors.password}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                      <div className="d-flex justify-content-end mt-1">
                        <Link to="/forgot-password" className="small text-decoration-none">
                          Esqueceu a senha?
                        </Link>
                      </div>
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                      className="w-100 mb-3"
                    >
                      {isSubmitting ? (
                        'Entrando...'
                      ) : (
                        <>
                          {FaSignInAlt({ className: "me-2" })} Entrar
                        </>
                      )}
                    </Button>
                    
                    <div className="text-center">
                      <p className="text-muted small mb-0">
                        {FaLock({ className: "me-1" })} Acesso seguro
                      </p>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;